import { Timestamp } from "firebase/firestore";
import { auth } from "../config/firebase";
import { logoutUser } from '../redux/reducers/authSlice';

export const getReadableErrorMessage = (errCode) => {
    // Return user-friendly error messages based on Firebase Auth error codes
    switch (errCode) {
        case 'auth/invalid-email':
            return 'The email address is badly formatted.';
        case 'auth/user-disabled':
            return 'This user has been disabled. Please contact support.';
        case 'auth/user-not-found':
            return 'No user found with this email. Please check and try again.';
        case 'auth/wrong-password':
            return 'The password is incorrect. Please try again.';
        case 'auth/weak-password':
            return 'The password is too weak. Please choose a stronger password.';
        case 'auth/email-already-in-use':
            return 'This email is already in use. Please try logging in or use a different email.';
        case 'auth/operation-not-allowed':
            return 'This operation is not allowed. Please contact support.';
        case 'auth/account-exists-with-different-credential':
            return 'An account already exists with the same email but different sign-in credentials. Try logging in with a different provider.';
        case 'auth/credential-already-in-use':
            return 'This credential is already in use. Please try a different method.';
        case 'auth/invalid-credential':
            return 'The provided credential is invalid. Please check and try again.';
        case 'auth/invalid-verification-code':
            return 'The verification code is invalid. Please check the code and try again.';
        case 'auth/invalid-verification-id':
            return 'The verification ID is invalid. Please check the ID and try again.';
        case 'auth/missing-android-pkg-name':
            return 'Missing Android package name. Please check your configuration.';
        case 'auth/missing-continue-uri':
            return 'Missing continue URL. Please check your configuration.';
        case 'auth/missing-ios-bundle-id':
            return 'Missing iOS bundle ID. Please check your configuration.';
        case 'auth/phone-number-already-exists':
            return 'This phone number is already associated with an existing account. Please try logging in.';
        case 'auth/invalid-phone-number':
            return 'The phone number is not valid. Please check and try again.';
        case 'auth/quota-exceeded':
            return 'The request quota has been exceeded. Please try again later.';
        case 'auth/timeout':
            return 'The request timed out. Please try again.';
        case 'auth/internal-error':
            return 'An internal error occurred. Please try again later.';
        case 'auth/invalid-api-key':
            return 'The API key is invalid. Please check your configuration.';
        case 'auth/app-not-authorized':
            return 'This app is not authorized to perform this operation.';
        case 'auth/invalid-user-token':
            return 'The user token is invalid. Please log in again.';
        case 'auth/expired-action-code':
            return 'The action code has expired. Please request a new one.';
        case 'auth/invalid-action-code':
            return 'The action code is invalid. Please check and try again.';
        case 'auth/invalid-email-verified':
            return 'The email is not verified. Please check your email for verification.';
        case 'auth/requires-recent-login':
            return 'This operation requires recent login. Please log in again and try again.';
        default:
            return `An unknown error occurred. Please try again later. \n Error: ${errCode}`;
    }
};


export const clearDataOnLogout = async (dispatch) => {
    await auth.signOut(); // Sign out using Firebase
    // Clear All Data from Redux
    dispatch(logoutUser());

};


// Calculate Discount 
export const calculateDiscountPercent = (price, specialPrice) => {
    let percentage = 0;
    percentage = (specialPrice / price) * 100;
    percentage = 100 - percentage;
    return parseFloat(percentage.toFixed(2));
};


// Function to safely convert timestamp or string to a Date object
export const convertToDate = (timeValue) => {
    if (!timeValue) return new Date(); // Default to current date if null/undefined

    if (timeValue instanceof Timestamp) {
        return timeValue.toDate(); // Convert Firebase Timestamp to JS Date
    } else if (typeof timeValue === "string") {
        return new Date(timeValue); // Convert string to JS Date
    } else if (typeof timeValue === "number") {
        return new Date(timeValue * (timeValue < 10000000000 ? 1000 : 1)); // Handle seconds vs milliseconds
    } else if (timeValue instanceof Date) {
        return timeValue; // Use the JS Date directly
    } else {
        return null;
    }
};

// Check the given date has passed
export const isPastDate = (isoString) => {
    return new Date(isoString) < new Date();
};

// Function to Convert Date timestamp to string
export const formatTimestamp = (timestamp) => {
    if (!timestamp) return null;

    if (timestamp instanceof Date) {
        return timestamp.toISOString(); // If already a Date object, format it
    }

    if (typeof timestamp === "string") {
        return new Date(timestamp).toISOString(); // Convert string to Date and format
    }

    if (typeof timestamp === "number") {
        return new Date(timestamp).toISOString(); // Convert Unix timestamp (ms) to Date and format
    }

    if (timestamp instanceof Timestamp) {
        return timestamp.toDate().toISOString(); // Handle Firestore Timestamp
    }

    return null; // Fallback in case of an unknown format
};


// Format a date string to this format (dd MMM, yyyy)
// This will get a date (String, number, Timestamp, js Date) and return a string in this format (dd-MMM, YYYY)

export const formatDateTODMY = (inputDate) => {
    if (!inputDate) return "";

    let date;

    // Handle Firestore Timestamp
    if (inputDate.seconds) {
        date = new Date(inputDate.seconds * 1000);
    } else {
        date = new Date(inputDate);
    }

    if (isNaN(date.getTime())) return "Invalid Date";

    return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    }).format(date);
};


export const formatDateTODMYHMA = (inputDate) => {
    if (!inputDate) return "";

    let date;

    // Handle Firestore Timestamp
    if (inputDate.seconds) {
        date = new Date(inputDate.seconds * 1000);
    } else {
        date = new Date(inputDate);
    }

    if (isNaN(date.getTime())) return "Invalid Date";

    return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    }).format(date);
};

// Function to get the month in "Feb" format
export const getMonthFromDate = (date) => {
    const parsedDate = convertToDate(date);
    return parsedDate ? parsedDate.toLocaleString("en-US", { month: "short" }) : null;
};

// Function to get the year in "YYYY" format
export const getYearFromDate = (date) => {
    const parsedDate = convertToDate(date);
    return parsedDate ? parsedDate.getFullYear() : null;
};

export const getDayFromDate = (date) => {
    const parsedDate = convertToDate(date);
    return parsedDate ? parsedDate.getDate() : null;
};